<template>
  <div
    v-tooltip="{
      content: buttonDescription,
      placement: forTable ? 'right' : 'left',
    }"
  >
    <icon-base
      class="cursor-pointer"
      data-cy="run-scorecard-button"
      :icon="hasBeenRun ? IconRerunScorecard : IconRunScorecard"
      height="24"
      width="24"
      @click="runScorecard"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { AnalyticsEvent } from "@/enums/analytics";
import { TYPE_GROUP, TYPE_INDIVIDUAL } from "@/helpers/constants/scorecards";
import { useAnalyticsStore } from "@/stores/analytics";

import IconRerunScorecard from "@/components/icons/IconRerunScorecard.vue";
import IconRunScorecard from "@/components/icons/IconRunScorecard.vue";

import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IScorecard | IScorecardGroup>,
    required: true,
  },
  isGroup: {
    type: Boolean,
    default: false,
  },
  forTable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  "scorecard:run": [id: string];
}>();

const { t } = useI18n();
const { commit } = useStore();
const { trackEvent } = useAnalyticsStore();

const hasBeenRun = computed(() => !!props.model.results?.length);

const buttonDescription = computed(() => {
  if (props.isGroup) {
    return hasBeenRun.value
      ? t("SCORECARD_GROUPS.RERUN_SC_GROUP")
      : t("SCORECARD_GROUPS.RUN_SC_GROUP");
  }
  return hasBeenRun.value ? t("SCORECARDS.RERUN_SC") : t("SCORECARDS.RUN_SC");
});

const runScorecard = () => {
  emit("scorecard:run", props.model.id);
  commit("scorecards/setRunScorecardIds", props.model.id);
  const runTypeMutation = props.isGroup ? TYPE_GROUP : TYPE_INDIVIDUAL;
  commit("scorecards/setRunScorecardType", runTypeMutation);

  if (props.isGroup) {
    trackEvent(AnalyticsEvent.ScorecardGroupRunTriggered, {
      scorecard_group_id: props.model.id,
      is_rerun: hasBeenRun.value,
    });
  } else {
    trackEvent(AnalyticsEvent.ScorecardRunTriggered, {
      scorecard_id: props.model.id,
      is_rerun: hasBeenRun.value,
    });
  }
};
</script>
